// src/pages/finance-portal/inventory/InventoryCounts.js
//
// Physical count workflow.
// List counts, create a count sheet, record counts line-by-line,
// submit for review, approve → posts variance adjustment.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem,
    Paper, Select, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatDate, formatQty,
} from './components/inventoryFormatters';

const COUNT_STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af' },
    READY_FOR_REVIEW: { bg: '#fed7aa', color: '#9a3412' },
    POSTED: { bg: '#dcfce7', color: '#166534' },
    CANCELLED: { bg: '#e5e7eb', color: '#374151' },
};

export default function InventoryCounts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [locations, setLocations] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        location_id: null, count_type: 'CYCLE',
        scheduled_date: new Date().toISOString().slice(0, 10),
        notes: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Detail dialog (count line editor)
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const fetchCounts = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axiosInstance.get('/api/finance/inventory/counts', { params });
            if (res.data?.success) setRows(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load counts');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchCounts(); }, [fetchCounts]);
    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/locations').then((r) => setLocations(r.data?.data || [])).catch(() => {});
    }, []);

    const openDetail = async (count) => {
        setDetailLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/counts/${count.id}`);
            if (res.data?.success) setDetail(res.data.data);
        } finally {
            setDetailLoading(false);
        }
    };

    const updateLineCount = async (lineId, countedQty) => {
        if (!detail) return;
        try {
            await axiosInstance.put(
                `/api/finance/inventory/counts/${detail.id}/lines/${lineId}`,
                { counted_qty: Number(countedQty) },
            );
            const res = await axiosInstance.get(`/api/finance/inventory/counts/${detail.id}`);
            if (res.data?.success) setDetail(res.data.data);
        } catch (e) {
            alert(e.response?.data?.message || 'Save failed');
        }
    };

    const submitCount = async () => {
        try {
            await axiosInstance.post(`/api/finance/inventory/counts/${detail.id}/submit`);
            fetchCounts();
            setDetail(null);
        } catch (e) {
            alert(e.response?.data?.message || 'Submit failed');
        }
    };

    const approveCount = async () => {
        if (!window.confirm('Approve this count and post variance adjustment to GL?')) return;
        try {
            await axiosInstance.post(`/api/finance/inventory/counts/${detail.id}/approve`);
            fetchCounts();
            setDetail(null);
        } catch (e) {
            alert(e.response?.data?.message || 'Approve failed');
        }
    };

    const handleCreate = async () => {
        if (!createForm.location_id) {
            alert('Location required');
            return;
        }
        setSaving(true);
        try {
            await axiosInstance.post('/api/finance/inventory/counts', createForm);
            setCreateOpen(false);
            setCreateForm({
                location_id: null, count_type: 'CYCLE',
                scheduled_date: new Date().toISOString().slice(0, 10),
                notes: '',
            });
            fetchCounts();
        } catch (e) {
            alert(e.response?.data?.message || 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    const chipStyle = (s) => {
        const c = COUNT_STATUS_COLORS[s] || { bg: '#e5e7eb', color: '#374151' };
        return { backgroundColor: c.bg, color: c.color };
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Physical Counts
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Generate count sheets, record actuals, and post variance to GL
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    New Count
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {Object.keys(COUNT_STATUS_COLORS).map((s) => (
                            <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Count #</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Scheduled</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">SKUs</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Variance</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Variance $</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? Array.from({ length: 4 }).map((_, i) => (
                            <TableRow key={i}><TableCell colSpan={9}><Skeleton height={32} /></TableCell></TableRow>
                        )) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={9} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                No counts yet
                            </TableCell></TableRow>
                        ) : rows.map((c) => (
                            <TableRow key={c.id} hover>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{c.count_number}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>{c.count_type?.replace('_', ' ')}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{c.location_name}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(c.scheduled_date)}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{c.skus_counted}</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: c.variance_count ? '#dc2626' : '#64748b' }} align="right">
                                    {c.variance_count}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                    {formatCurrency(c.total_variance_value)}
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={c.status?.replace('_', ' ')}
                                        style={chipStyle(c.status)}
                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openDetail(c)}>
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Create */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>New Count Sheet</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Location</InputLabel>
                            <Select value={createForm.location_id || ''} label="Location"
                                onChange={(e) => setCreateForm((f) => ({ ...f, location_id: e.target.value }))}>
                                {locations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Count Type</InputLabel>
                            <Select value={createForm.count_type} label="Count Type"
                                onChange={(e) => setCreateForm((f) => ({ ...f, count_type: e.target.value }))}>
                                {['FULL_PHYSICAL', 'CYCLE', 'SPOT', 'ABC_A', 'ABC_B', 'ABC_C'].map((t) => (
                                    <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField size="small" fullWidth type="date" label="Scheduled Date"
                            value={createForm.scheduled_date}
                            onChange={(e) => setCreateForm((f) => ({ ...f, scheduled_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                        <TextField size="small" fullWidth multiline rows={2} label="Notes"
                            value={createForm.notes}
                            onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={saving}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {saving ? 'Creating…' : 'Generate Count Sheet'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail / Line editor */}
            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
                {detail && (
                    <>
                        <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>
                            {detail.count_number} — {detail.location_name}
                            <Chip size="small" label={detail.status?.replace('_', ' ')}
                                style={chipStyle(detail.status)}
                                sx={{ ml: 2, fontWeight: 600 }} />
                        </DialogTitle>
                        <DialogContent dividers>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Expected</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Counted</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Variance</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Variance $</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(detail.lines || []).map((ln) => (
                                        <TableRow key={ln.id}>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                                {ln.product_sku}
                                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', color: '#64748b' }}>
                                                    {ln.product_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(ln.expected_qty)}</TableCell>
                                            <TableCell align="right">
                                                <TextField size="small" type="number" sx={{ width: 100 }}
                                                    disabled={['POSTED', 'CANCELLED'].includes(detail.status)}
                                                    defaultValue={ln.counted_qty ?? ''}
                                                    onBlur={(e) => e.target.value !== '' && updateLineCount(ln.id, e.target.value)} />
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily: FONT_FAMILY, fontWeight: 600,
                                                color: Number(ln.variance_qty) > 0 ? '#166534' : Number(ln.variance_qty) < 0 ? '#991b1b' : '#64748b',
                                            }} align="right">
                                                {ln.counted_qty != null ? (Number(ln.variance_qty) > 0 ? '+' : '') + formatQty(ln.variance_qty) : '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                                {ln.counted_qty != null ? formatCurrency(ln.variance_value) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDetail(null)}>Close</Button>
                            {['DRAFT', 'IN_PROGRESS'].includes(detail.status) && (
                                <Button variant="outlined" onClick={submitCount}
                                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                                    Submit for Review
                                </Button>
                            )}
                            {detail.status === 'READY_FOR_REVIEW' && (
                                <Button variant="contained" color="success" onClick={approveCount}
                                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                                    Approve &amp; Post
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
